import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Opportunity } from '../types';
import { useAuth } from '../contexts/AuthContext';
import SubmitOpportunityModal from './SubmitOpportunityModal';
import { BriefcaseIcon, TargetIcon, ZapIcon, BookIcon, TrophyIcon } from './Icons';
import { BACKEND_URL } from '../lib/backendUrl';
import '../styles/OpportunitiesSection.css';

const API_URL = BACKEND_URL;

const OpportunitiesSection: React.FC = React.memo(() => {
  const { user, token } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'internship' | 'challenge' | 'hackathon' | 'workshop'>('all');

  useEffect(() => {
    fetchOpportunities();
  }, [token]); // Re-fetch when auth state changes

  const fetchOpportunities = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Prepare headers - include token if authenticated
      const config: any = {
        headers: {
          'Content-Type': 'application/json',
        }
      };

      // Include authorization header if token is available
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.get(`${API_URL}/api/opportunities/approved`, config);
      setOpportunities(response.data || []);
    } catch (error: any) {
      // Only show error if it's not a 401/403/404 (these are expected for auth failures)
      if (error.response?.status !== 401 && error.response?.status !== 403 && error.response?.status !== 404) {
        setError('Failed to load opportunities');
      }
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleOpportunitySubmitted = useCallback(() => {
    // Don't add pending opportunities to the displayed list
    // They will only appear in the Career Hub after admin approval
    // The notification is created for admin review
    setShowSubmitModal(false);
  }, []);

  const getOpportunityIcon = useMemo(() => {
    return (type: string) => {
      switch (type) {
        case 'internship':
          return <BriefcaseIcon size={16} color="#00ffff" />;
        case 'challenge':
          return <TargetIcon size={16} color="#ff00ff" />;
        case 'hackathon':
          return <ZapIcon size={16} color="#7000ff" />;
        case 'workshop':
          return <BookIcon size={16} color="#10b981" />;
        case 'competition':
          return <TrophyIcon size={16} color="#ff00ff" />;
        default:
          return <BriefcaseIcon size={16} color="#00ffff" />;
      }
    };
  }, []);

  const getOpportunityColor = useMemo(() => {
    return (type: string): 'cyan' | 'magenta' | 'purple' | 'green' => {
      switch (type) {
        case 'internship':
          return 'cyan';
        case 'challenge':
          return 'magenta';
        case 'hackathon':
          return 'purple';
        case 'workshop':
          return 'green';
        case 'competition':
          return 'magenta';
        default:
          return 'cyan';
      }
    };
  }, []);

  // Memoize filtered opportunities to prevent unnecessary recalculations
  const filteredOpportunities = useMemo(() => 
    activeFilter === 'all'
      ? opportunities
      : opportunities.filter(opp => opp.type === activeFilter),
    [opportunities, activeFilter]
  );

  const filterOptions: Array<'all' | 'internship' | 'challenge' | 'hackathon' | 'workshop'> = [
    'all',
    'internship',
    'challenge',
    'hackathon',
    'workshop'
  ];

  return (
    <section className="career-hub-section">
      <div className="career-hub-header">
        <h2 className="career-hub-title">Career_Hub_Uplink</h2>
        
        {user && (
          <button 
            className="post-opportunity-btn"
            onClick={() => setShowSubmitModal(true)}
          >
            <span className="btn-icon">+</span> POST_OPPORTUNITY
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="career-hub-filters">
        {filterOptions.map((filter) => (
          <button
            key={filter}
            className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
            onClick={() => setActiveFilter(filter)}
          >
            {filter === 'all' ? 'ALL' : filter.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Opportunities List */}
      <div className="opportunities-container">
        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>INITIALIZING_CAREER_HUB...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={fetchOpportunities} className="retry-btn">RETRY</button>
          </div>
        ) : filteredOpportunities.length === 0 ? (
          <div className="empty-state">
            <p>NO {activeFilter.toUpperCase()} OPPORTUNITIES AVAILABLE</p>
            {user && <p className="empty-hint">Be the first to post! Click the + button above</p>}
          </div>
        ) : (
          <div className="opportunities-list">
            {filteredOpportunities.map((opp) => (
              <OpportunityRow
                key={opp.id}
                opportunity={opp}
                icon={getOpportunityIcon(opp.type)}
                color={getOpportunityColor(opp.type)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <SubmitOpportunityModal
          onClose={() => setShowSubmitModal(false)}
          onSuccess={handleOpportunitySubmitted}
        />
      )}
    </section>
  );
});

interface OpportunityRowProps {
  opportunity: Opportunity;
  icon: React.ReactNode;
  color: 'cyan' | 'magenta' | 'purple' | 'green';
}

const OpportunityRow: React.FC<OpportunityRowProps> = ({ opportunity, icon, color }) => {
  const [showDetails, setShowDetails] = useState(false);

  const colorMap = {
    cyan: '#00f3ff',
    magenta: '#ff00ff',
    purple: '#7000ff',
    green: '#10b981'
  };

  const colorClass = {
    cyan: 'border-cyan',
    magenta: 'border-magenta',
    purple: 'border-purple',
    green: 'border-green'
  }[color];

  return (
    <>
      <div className={`opportunity-row ${colorClass}`}>
        <div className="row-icon">
          {icon}
        </div>
        
        <div className="row-content">
          <h4 className="row-title">{opportunity.title}</h4>
          <p className="row-meta">
            <span className="material-symbols-outlined">account_circle</span>
            Posted By: {opportunity.postedBy || 'Anonymous_Node'}
          </p>
        </div>

        <button 
          className="details-btn"
          onClick={() => setShowDetails(!showDetails)}
        >
          DETAILS
        </button>
      </div>

      {showDetails && (
        <DetailsPanel
          opportunity={opportunity}
          onClose={() => setShowDetails(false)}
          color={color}
        />
      )}
    </>
  );
};

interface DetailsPanelProps {
  opportunity: Opportunity;
  onClose: () => void;
  color: 'cyan' | 'magenta' | 'purple' | 'green';
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({ opportunity, onClose, color }) => {
  const colorMap = {
    cyan: '#00f3ff',
    magenta: '#ff00ff',
    purple: '#7000ff',
    green: '#10b981'
  };

  return (
    <div className={`details-panel border-${color}`}>
      <div className="details-header">
        <h4>{opportunity.title}</h4>
        <button onClick={onClose} className="close-details">✕</button>
      </div>

      <div className="details-body">
        {opportunity.description && (
          <div className="detail-section">
            <h5>Description</h5>
            <p>{opportunity.description}</p>
          </div>
        )}

        {opportunity.category && (
          <div className="detail-section">
            <h5>Category</h5>
            <p>{opportunity.category}</p>
          </div>
        )}

        {opportunity.deadline && (
          <div className="detail-section">
            <h5>Deadline</h5>
            <p>{new Date(opportunity.deadline).toLocaleDateString()}</p>
          </div>
        )}

        {opportunity.company && (
          <div className="detail-section">
            <h5>Company</h5>
            <p>{opportunity.company}</p>
          </div>
        )}

        {opportunity.link && (
          <div className="detail-section">
            <a href={opportunity.link} target="_blank" rel="noopener noreferrer" className="apply-link">
              APPLY NOW →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunitiesSection;
